import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Moon, Sun, RefreshCw, ArrowLeft, Filter, CheckCircle, Clock, XCircle } from 'lucide-react';
import { bookingAPI, greeterAPI } from '../../services/api';
import { formatCurrency } from '../../utils/formatters';
import Loader from '../common/Loader';
import BookingDetailCard from '../common/BookingDetailCard';

// Icons

const ViewIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const CreditCardIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
  </svg>
);

const BookingsIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const UserBookings = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showBookingDetail, setShowBookingDetail] = useState(false);
  const [greeterInfoModal, setGreeterInfoModal] = useState({ isOpen: false, greeter: null });
  const [filters, setFilters] = useState({
    status: 'all',
    page: 1,
    limit: 20
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('userDarkMode');
    return saved ? JSON.parse(saved) : false;
  });

  const fetchBookings = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await bookingAPI.getUserBookings(filters);
      
      if (response.success) {
        setBookings(response.data.bookings);
        setPagination(response.data.pagination);
      } else {
        toast.error('Failed to fetch bookings');
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Failed to fetch bookings');
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      page: 1 // Reset to first page when filters change
    }));
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({
      ...prev,
      page
    }));
  };

  const handleRefresh = () => {
    fetchBookings();
  };

  const handleViewBooking = (booking) => {
    setSelectedBooking(booking);
    setShowBookingDetail(true);
  };

  const handleCloseBookingDetail = () => {
    setShowBookingDetail(false);
    setSelectedBooking(null);
  };

  const handleAssignmentClick = async (booking) => {
    if (booking.isAssigned && booking.assignedGreeter) {
      try {
        const response = await greeterAPI.getGreeterInfoForBooking(booking._id);
        if (response.success) {
          setGreeterInfoModal({ isOpen: true, greeter: response.data.greeter });
        } else {
          toast.error('Failed to fetch greeter information');
        }
      } catch (error) {
        console.error('Error fetching greeter info:', error);
        toast.error('Failed to fetch greeter information');
      }
    }
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode
        ? 'bg-skyone-page-dark'
        : 'bg-gradient-to-br from-skyone-page via-white to-primary-50/50'
    }`}>
      {/* Header — surface tier between page and cards */}
      <div className={`shadow-lg border-b transition-colors duration-300 ${
        isDarkMode
          ? 'bg-skyone-surface-dark border-skyone-border-dark'
          : 'bg-white border-gray-200'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-8">
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white transition-colors duration-300 ${
                  isDarkMode
                    ? 'bg-gradient-to-br from-primary-600 to-primary-800 ring-2 ring-accent-500/25'
                    : 'bg-gradient-to-br from-primary-600 to-primary-800'
                }`}>
                  <BookingsIcon />
                </div>
              </div>
              <div>
                <h1 className={`text-3xl font-bold transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  All Bookings
                </h1>
                <p className={`mt-1 transition-colors duration-300 ${
                  isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-600'
                }`}>View and manage all your airport meet & greet bookings</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {/* Dark Mode Toggle */}
              <button
                onClick={() => {
                  const newMode = !isDarkMode;
                  setIsDarkMode(newMode);
                  localStorage.setItem('userDarkMode', JSON.stringify(newMode));
                }}
                className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-all duration-300 ${
                  isDarkMode 
                    ? 'bg-skyone-inset-dark text-white hover:bg-skyone-border-dark border border-skyone-border-dark' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300 border border-gray-300'
                }`}
                title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDarkMode ? (
                  <>
                    <Sun className="w-4 h-4 mr-2" />
                    Light
                  </>
                ) : (
                  <>
                    <Moon className="w-4 h-4 mr-2" />
                    Dark
                  </>
                )}
              </button>
              
              <button
                onClick={handleRefresh}
                disabled={isLoading}
                className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 shadow-sm hover:shadow-md flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                  isDarkMode
                    ? 'bg-primary-600 hover:bg-primary-500 text-white border border-primary-500/80 shadow-lg shadow-primary-950/30'
                    : 'bg-primary-600 hover:bg-primary-700 text-white'
                }`}
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                <span>{isLoading ? 'Refreshing...' : 'Refresh'}</span>
              </button>
              <button
                onClick={() => navigate('/user/dashboard')}
                className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 shadow-sm hover:shadow-md flex items-center space-x-2 ${
                  isDarkMode
                    ? 'bg-skyone-inset-dark hover:bg-skyone-border-dark text-white border border-skyone-border-dark'
                    : 'bg-gray-600 hover:bg-gray-700 text-white'
                }`}
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back to Dashboard</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className={`shadow-lg rounded-xl border mb-6 transition-colors duration-300 ${
          isDarkMode
            ? 'bg-skyone-card-dark border-skyone-border-dark shadow-black/20'
            : 'bg-white/90 border-gray-100 backdrop-blur-sm'
        }`}>
          <div className="px-6 py-6">
            <div className="flex items-center space-x-2 mb-6">
              <Filter className={`w-5 h-5 transition-colors duration-300 ${
                isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-600'
              }`} />
              <h3 className={`text-xl font-bold transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Filter Bookings
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className={`block text-sm font-semibold mb-3 transition-colors duration-300 ${
                  isDarkMode ? 'text-zinc-300' : 'text-gray-700'
                }`}>
                  Status
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange({ status: e.target.value })}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-colors duration-200 ${
                    isDarkMode
                      ? 'bg-skyone-inset-dark border-skyone-border-dark text-white focus:ring-primary-500'
                      : 'bg-white border-gray-300 text-gray-900 focus:ring-primary-500'
                  }`}
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="failed">Failed</option>
                  <option value="expired">Expired</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Results Summary */}
        <div className={`flex items-center justify-between rounded-lg px-6 py-4 mb-6 shadow-sm border transition-colors duration-300 ${
          isDarkMode
            ? 'bg-skyone-card-dark border-skyone-border-dark shadow-black/15'
            : 'bg-white/90 border-gray-100 backdrop-blur-sm'
        }`}>
          <div className={`text-sm font-medium transition-colors duration-300 ${
            isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-700'
          }`}>
            Showing <span className={`font-semibold transition-colors duration-300 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>{bookings.length}</span> of <span className={`font-semibold transition-colors duration-300 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>{pagination.totalCount}</span> bookings
          </div>
          <div className={`text-sm font-medium transition-colors duration-300 ${
            isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-700'
          }`}>
            Page <span className={`font-semibold transition-colors duration-300 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>{pagination.currentPage}</span> of <span className={`font-semibold transition-colors duration-300 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>{pagination.totalPages}</span>
          </div>
        </div>

        {/* Loading State */}
        {isLoading ? (
          <div className="flex justify-center py-16">
            <div className="text-center">
              <Loader size="lg" text="Loading bookings..." isDarkMode={isDarkMode} />
            </div>
          </div>
        ) : bookings.length === 0 ? (
          /* Empty State */
          <div className="text-center py-16">
            <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 transition-colors duration-300 ${
              isDarkMode ? 'bg-skyone-inset-dark text-accent-400/90' : 'bg-gray-100 text-primary-600'
            }`}>
              <BookingsIcon />
            </div>
            <h3 className={`text-xl font-semibold mb-2 transition-colors duration-300 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>No bookings found</h3>
            <p className={`text-lg mb-6 transition-colors duration-300 ${
              isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-500'
            }`}>
              No bookings match your current filters.
            </p>
            <button
              onClick={() => navigate('/user/dashboard')}
              className={`px-6 py-3 rounded-lg text-sm font-medium transition-all duration-300 shadow-sm hover:shadow-md ${
                isDarkMode
                  ? 'bg-primary-600 hover:bg-primary-500 text-white border border-primary-500/80'
                  : 'bg-primary-600 hover:bg-primary-700 text-white'
              }`}
            >
              Create New Booking
            </button>
          </div>
        ) : (
          /* Bookings List */
          <div className={`shadow-lg rounded-xl border overflow-hidden transition-colors duration-300 ${
            isDarkMode
              ? 'bg-skyone-card-dark border-skyone-border-dark shadow-black/20'
              : 'bg-white/90 border-gray-100 backdrop-blur-sm'
          }`}>
            <div className="overflow-x-auto">
              <table className={`min-w-full divide-y transition-colors duration-300 ${
                isDarkMode ? 'divide-skyone-border-dark' : 'divide-gray-200'
              }`}>
                <thead className={`transition-colors duration-300 ${
                  isDarkMode ? 'bg-skyone-inset-dark' : 'bg-gray-50'
                }`}>
                  <tr>
                    <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider transition-colors duration-300 ${
                      isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-600'
                    }`}>
                      Service
                    </th>
                    <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider transition-colors duration-300 ${
                      isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-600'
                    }`}>
                      Amount
                    </th>
                    <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider transition-colors duration-300 ${
                      isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-600'
                    }`}>
                      Status
                    </th>
                    <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider transition-colors duration-300 ${
                      isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-600'
                    }`}>
                      Date
                    </th>
                    <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider transition-colors duration-300 ${
                      isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-600'
                    }`}>
                      Assignment
                    </th>
                    <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider transition-colors duration-300 ${
                      isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-600'
                    }`}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className={`divide-y transition-colors duration-300 ${
                  isDarkMode 
                    ? 'bg-skyone-card-dark divide-skyone-border-dark' 
                    : 'bg-white divide-gray-200'
                }`}>
                  {bookings.map((booking) => (
                    <tr key={booking._id} className={`transition-colors duration-150 ${
                      isDarkMode ? 'hover:bg-skyone-inset-dark' : 'hover:bg-gray-50'
                    }`}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`text-sm font-semibold transition-colors duration-300 ${
                              isDarkMode ? 'text-white' : 'text-gray-900'
                            }`}>
                              {booking.serviceDetails?.levelName 
                                ? `${booking.serviceDetails.categoryName || booking.serviceType} - ${booking.serviceDetails.levelName}`
                                : booking.serviceType
                              }
                            </div>
                          </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm font-semibold transition-colors duration-300 ${
                          isDarkMode ? 'text-white' : 'text-gray-900'
                        }`}>
                          {formatCurrency(booking.amount, booking.currency || 'GBP')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full border transition-colors duration-300 ${
                          booking.status === 'paid' 
                            ? isDarkMode 
                              ? 'bg-green-900 text-green-200 border-green-700'
                              : 'bg-green-100 text-green-800 border-green-200'
                            : booking.status === 'pending'
                            ? isDarkMode
                              ? 'bg-yellow-900 text-yellow-200 border-yellow-700'
                              : 'bg-yellow-100 text-yellow-800 border-yellow-200'
                            : booking.status === 'failed'
                            ? isDarkMode
                              ? 'bg-red-900 text-red-200 border-red-700'
                              : 'bg-red-100 text-red-800 border-red-200'
                            : isDarkMode
                              ? 'bg-skyone-inset-dark text-zinc-200 border-skyone-border-dark'
                              : 'bg-gray-100 text-gray-800 border-gray-200'
                        }`}>
                          {booking.status === 'paid' && (
                            <>
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Paid
                            </>
                          )}
                          {booking.status === 'pending' && (
                            <>
                              <Clock className="h-3 w-3 mr-1" />
                              Pending
                            </>
                          )}
                          {booking.status === 'failed' && (
                            <>
                              <XCircle className="h-3 w-3 mr-1" />
                              Failed
                            </>
                          )}
                          {booking.status === 'expired' && (
                            <>
                              <Clock className="h-3 w-3 mr-1" />
                              Expired
                            </>
                          )}
                          {!['paid', 'pending', 'failed', 'expired'].includes(booking.status) && (
                            <>
                              <XCircle className="h-3 w-3 mr-1" />
                              {booking.status}
                            </>
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm transition-colors duration-300 ${
                          isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-600'
                        }`}>
                          {new Date(booking.createdAt).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {booking.isAssigned ? (
                          <button
                            onClick={() => handleAssignmentClick(booking)}
                            className={`inline-flex items-center px-3 py-1 text-xs font-semibold rounded-lg border transition-all duration-200 ${
                              isDarkMode 
                                ? 'text-green-200 bg-green-900 border-green-700 hover:bg-green-800 hover:text-green-100' 
                                : 'text-green-700 bg-green-100 border-green-300 hover:bg-green-200 hover:text-green-800'
                            }`}
                          >
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            Assigned
                          </button>
                        ) : (
                          <span className={`text-xs transition-colors duration-300 ${
                            isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-500'
                          }`}>Not Assigned</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-3">
                          <button 
                            onClick={() => handleViewBooking(booking)}
                            className={`font-semibold transition-colors duration-150 flex items-center space-x-1 ${
                              isDarkMode
                                ? 'text-accent-400 hover:text-accent-300'
                                : 'text-primary-600 hover:text-primary-800'
                            }`}
                          >
                            <ViewIcon />
                            <span>View</span>
                          </button>
                          {booking.status === 'pending' && (
                            <a 
                              href={booking.paymentLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`font-semibold transition-colors duration-150 flex items-center space-x-1 ${
                                isDarkMode 
                                  ? 'text-green-400 hover:text-green-300' 
                                  : 'text-green-600 hover:text-green-800'
                              }`}
                            >
                              <CreditCardIcon />
                              <span>Pay</span>
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className={`shadow-lg rounded-xl border mt-8 transition-colors duration-300 ${
            isDarkMode
              ? 'bg-skyone-card-dark border-skyone-border-dark shadow-black/20'
              : 'bg-white/90 border-gray-100 backdrop-blur-sm'
          }`}>
            <div className="px-6 py-4">
              <div className="flex flex-1 justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={!pagination.hasPrevPage}
                  className={`relative inline-flex items-center rounded-lg border px-4 py-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 ${
                    isDarkMode 
                      ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-300 hover:bg-skyone-border-dark' 
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <ChevronLeftIcon />
                  <span className="ml-2">Previous</span>
                </button>
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={!pagination.hasNextPage}
                  className={`relative ml-3 inline-flex items-center rounded-lg border px-4 py-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 ${
                    isDarkMode 
                      ? 'border-skyone-border-dark bg-skyone-inset-dark text-gray-300 hover:bg-skyone-border-dark' 
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className="mr-2">Next</span>
                  <ChevronRightIcon />
                </button>
              </div>
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div>
                  <p className={`text-sm font-medium transition-colors duration-300 ${
                    isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-700'
                  }`}>
                    Showing{' '}
                    <span className={`font-semibold transition-colors duration-300 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {(pagination.currentPage - 1) * pagination.limit + 1}
                    </span>{' '}
                    to{' '}
                    <span className={`font-semibold transition-colors duration-300 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {Math.min(pagination.currentPage * pagination.limit, pagination.totalCount)}
                    </span>{' '}
                    of{' '}
                    <span className={`font-semibold transition-colors duration-300 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>{pagination.totalCount}</span>{' '}
                    results
                  </p>
                </div>
                <div>
                  <nav className="isolate inline-flex -space-x-px rounded-lg shadow-sm" aria-label="Pagination">
                    <button
                      onClick={() => handlePageChange(pagination.currentPage - 1)}
                      disabled={!pagination.hasPrevPage}
                      className={`relative inline-flex items-center rounded-l-lg px-3 py-2 ring-1 ring-inset focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 ${
                        isDarkMode 
                          ? 'text-gray-400 ring-skyone-border-dark hover:bg-skyone-inset-dark' 
                          : 'text-gray-400 ring-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <span className="sr-only">Previous</span>
                      <ChevronLeftIcon />
                    </button>
                    
                    {/* Page Numbers */}
                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                      const pageNum = Math.max(1, pagination.currentPage - 2) + i;
                      if (pageNum > pagination.totalPages) return null;
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold focus:z-20 focus:outline-offset-0 transition-all duration-200 ${
                            pageNum === pagination.currentPage
                              ? isDarkMode
                                ? 'z-10 bg-primary-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500'
                                : 'z-10 bg-primary-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600'
                              : isDarkMode
                                ? 'text-zinc-300 ring-1 ring-inset ring-skyone-border-dark hover:bg-skyone-inset-dark'
                                : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => handlePageChange(pagination.currentPage + 1)}
                      disabled={!pagination.hasNextPage}
                      className={`relative inline-flex items-center rounded-r-lg px-3 py-2 ring-1 ring-inset focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 ${
                        isDarkMode 
                          ? 'text-gray-400 ring-skyone-border-dark hover:bg-skyone-inset-dark' 
                          : 'text-gray-400 ring-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <span className="sr-only">Next</span>
                      <ChevronRightIcon />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Booking Detail Modal */}
      <BookingDetailCard
        booking={selectedBooking}
        isOpen={showBookingDetail}
        onClose={handleCloseBookingDetail}
        isDarkMode={isDarkMode}
      />

      {/* Greeter Info Modal */}
      {greeterInfoModal.isOpen && (
        <div className="fixed inset-0 bg-skyone-page-dark/60 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
          <div className={`relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md transition-colors duration-300 ${
            isDarkMode ? 'bg-skyone-card-dark border-skyone-border-dark shadow-black/40' : 'bg-white border-gray-200'
          }`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className={`text-lg font-semibold transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Assigned Greeter
              </h3>
              <button
                onClick={() => setGreeterInfoModal({ isOpen: false, greeter: null })}
                className={`transition-colors duration-200 ${
                  isDarkMode ? 'text-skyone-muted-dark hover:text-white' : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {greeterInfoModal.greeter && (
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors duration-300 ${
                      isDarkMode ? 'bg-green-800' : 'bg-green-100'
                    }`}>
                      <svg className={`w-6 h-6 transition-colors duration-300 ${
                        isDarkMode ? 'text-green-300' : 'text-green-600'
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                  </div>
                  <div>
                    <h4 className={`text-lg font-semibold transition-colors duration-300 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {greeterInfoModal.greeter.firstName} {greeterInfoModal.greeter.lastName}
                    </h4>
                    <p className={`text-sm transition-colors duration-300 ${
                      isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-500'
                    }`}>{greeterInfoModal.greeter.email}</p>
                  </div>
                </div>
                
                <div className={`border-t pt-4 transition-colors duration-300 ${
                  isDarkMode ? 'border-skyone-border-dark' : 'border-gray-200'
                }`}>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className={`font-medium transition-colors duration-300 ${
                        isDarkMode ? 'text-zinc-300' : 'text-gray-700'
                      }`}>Phone:</span>
                      <p className={`transition-colors duration-300 ${
                        isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-600'
                      }`}>{greeterInfoModal.greeter.phoneNumber}</p>
                    </div>
                    <div>
                      <span className={`font-medium transition-colors duration-300 ${
                        isDarkMode ? 'text-zinc-300' : 'text-gray-700'
                      }`}>Status:</span>
                      <p className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium transition-colors duration-300 ${
                        greeterInfoModal.greeter.isActive 
                          ? isDarkMode
                            ? 'bg-green-800 text-green-200' 
                            : 'bg-green-100 text-green-800'
                          : isDarkMode
                            ? 'bg-red-800 text-red-200'
                            : 'bg-red-100 text-red-800'
                      }`}>
                        {greeterInfoModal.greeter.isActive ? 'Active' : 'Inactive'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setGreeterInfoModal({ isOpen: false, greeter: null })}
                className={`px-4 py-2 rounded-md transition-colors duration-200 ${
                  isDarkMode
                    ? 'bg-skyone-inset-dark text-zinc-200 border border-skyone-border-dark hover:bg-skyone-border-dark'
                    : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                }`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserBookings;
